// @ts-check
import {gmAPI, MapService, Markers} from '@/services/MapService';
import {Graphics} from '@/services/GraphicService';
import {blue} from '../colors';
import {ref} from 'vue';

const toMeters = (/** @type {number} */ miles) => miles * 1609.34;
const toMiles = (/** @type {number} */ meters) => meters / 1609.34;
const feetToMeters = (/** @type {number} */ feet) => feet * 0.3048;
const toFeet = (/** @type {number} */ meters) => meters / 0.3048;

/** @type {number} the default circle radius in METERS (500 ft) */
const DEFAULT_CIRCLE_RADIUS = feetToMeters(500);

/** @type {google.maps.MapsEventListener} */
let _clickListener = null;
/** @type {google.maps.Circle} */
let _circle = null;
/** @type {google.maps.Polyline} */
let _polyline = null;
/** @type {import('vue').Ref<number>} current circle radius in METERS */
const circleRadius = ref(DEFAULT_CIRCLE_RADIUS);

/**
 * Calculate the path of the circle
 * @return {google.maps.LatLng[]}
 */
const circlePath = () => {
  const numPts = 512;
  const path = [];
  for (let i = 0; i < numPts; i++) {
    const latLng = gmAPI.geometry.spherical.computeOffset(_circle.getCenter(), _circle.getRadius(), i * 360 / numPts);
    path.push(latLng);
  }
  path.push(path[0]); // close the loop
  return path;
};

/**
 * Draw a dashed-line polyline from the circle's path
 */
const drawPolylineFromCircle = () => {
  const points = circlePath();
  if (_polyline) {
    _polyline.setPath(points);
  } else {
    _polyline = new gmAPI.Polyline({
      map: MapService.getMap(),
      path: points,
      strokeOpacity: 0,
      strokeWeight: 2,
      icons: [
        {
          icon: {
            path: 'M 0,-1 0,1',
            strokeOpacity: 1,
            strokeColor: blue,
            scale: 4,
          },
          offset: '0',
          repeat: '20px',
        },
      ],
    });
  }
};

/**
 * Creates a gmAPI.Circle from the center and radius
 * @param {number} lat
 * @param {number} lng
 * @param {number} radius
 */
const createCircle = (lat, lng, radius) => {
  if (radius == null || !lat || !lng) return;

  _circle = new gmAPI.Circle({
    strokeOpacity: 0.2,
    fillOpacity: 0.2,
    fillColor: blue,
    strokeColor: blue,
    map: MapService.getMap(),
    center: {lat, lng},
    radius,
    editable: true,
    clickable: false,
  });

  _circle.addListener('radius_changed', () => {
    circleRadius.value = _circle.getRadius();
    drawPolylineFromCircle();
  });
  _circle.addListener('center_changed', () => {
    drawPolylineFromCircle();
  });

  drawPolylineFromCircle();
};


/**
 * Add or remove click listeners
 * @param {boolean} isListening
 */
const setListeners = (isListening) => {
  if (isListening) {
    _clickListener = MapService.getMap().addListener('click', (event) => {
      if (_circle) {
        _circle.setMap(null);
        _circle = null;
      }
      createCircle(parseFloat(event.latLng.lat()), parseFloat(event.latLng.lng()), circleRadius.value);
      toggleCircleMode(false);
    });
  } else {
    if (_clickListener) {
      _clickListener.remove();
      _clickListener = null;
    }
  }
};

/**
 * Toggle whether the map is in place radius marker mode
 * @param {boolean} isListening
 */
const toggleCircleMode = (isListening) => {
  MapService.setCursor(isListening ? 'crosshair': 'default');
  Markers.setAllClickable(!isListening);
  Graphics.setAllClickable(!isListening);
  setListeners(isListening);
};

/**
 * Set the radius of the circle IN METERS
 * @param {number} radius
 */
const setRadius = (radius) => {
  circleRadius.value = radius;
  if (!_circle) return;
  _circle.setRadius(radius);
  drawPolylineFromCircle();
};

const enterCircleMode = () => {
  toggleCircleMode(true);
};

const exitCircleMode = () => {
  toggleCircleMode(false);
};

const setEditable = (isEditable) => {
  _circle?.setEditable(isEditable);
};

const clearCircle = () => {
  exitCircleMode();
  circleRadius.value = DEFAULT_CIRCLE_RADIUS;
  if (_circle) {
    _circle.setMap(null);
    _circle = null;
  }
  if (_polyline) {
    _polyline.setMap(null);
    _polyline = null;
  }
};

export {
  enterCircleMode,
  exitCircleMode,
  clearCircle,
  toMeters,
  toMiles,
  feetToMeters,
  toFeet,
  setRadius,
  setEditable,
  circleRadius,
  DEFAULT_CIRCLE_RADIUS,
};
